import { useState, type FormEvent } from 'react';
import type { PublicInstitution, VerificationMethod } from '@/types';
import Button from '@/components/base/Button';

/** The verify-using choices, in the order shown. Messages match the API's. */
const METHODS: Record<
  VerificationMethod,
  { label: string; inputLabel: string; placeholder: string; missing: string; submit: string }
> = {
  matric: {
    label: 'Matriculation Number',
    inputLabel: 'Enter Matriculation Number',
    placeholder: 'RSU/18/001',
    missing: 'Please enter a matriculation number.',
    submit: 'VERIFY CERTIFICATE',
  },
  certificate: {
    label: 'Certificate Code',
    inputLabel: 'Enter Certificate Code',
    placeholder: 'GF-RSU-2026-000125',
    missing: 'Please enter a certificate code.',
    submit: 'VERIFY CERTIFICATE',
  },
  result: {
    label: 'Result Code',
    inputLabel: 'Enter Statement of Result Verification Code',
    placeholder: 'd754fc00-cf5c-4139-aebe-0f3d1a2cb340',
    missing: 'Please enter a result verification code.',
    submit: 'VERIFY STATEMENT OF RESULT',
  },
};

export interface VerificationInput {
  institutionId: string;
  method: VerificationMethod;
  reference: string;
}

interface Props {
  institutions: PublicInstitution[];
  institutionsLoading?: boolean;
  /** Set when the institution list couldn't be loaded. */
  institutionsError?: string;
  onRetryInstitutions?: () => void;
  onSubmit: (institutionId: string, method: VerificationMethod, reference: string) => void;
  loading: boolean;
  /** Blocks submitting, e.g. during maintenance. */
  disabled?: boolean;
  /** What was entered last time, to restore after a rejected submission. */
  initialValues?: VerificationInput | null;
  /** An error from the server about the last submission. */
  serverError?: string;
}

export default function VerificationForm({
  institutions,
  institutionsLoading = false,
  institutionsError,
  onRetryInstitutions,
  onSubmit,
  loading,
  disabled = false,
  initialValues,
  serverError = '',
}: Props) {
  const [institutionId, setInstitutionId] = useState(initialValues?.institutionId ?? '');
  const [method, setMethod] = useState<VerificationMethod>(initialValues?.method ?? 'matric');
  const [reference, setReference] = useState(initialValues?.reference ?? '');
  const [error, setError] = useState(serverError);

  const current = METHODS[method];

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    // A pre-filled link can name an institution that isn't listed; the dropdown shows none.
    if (!institutions.some((i) => i.id === institutionId)) {
      setError('Please select an institution.');
      return;
    }
    if (!reference.trim()) {
      setError(current.missing);
      return;
    }
    setError('');
    onSubmit(institutionId, method, reference.trim());
  }

  function switchMethod(m: VerificationMethod) {
    setMethod(m);
    setReference('');
    setError('');
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Institution */}
      <div>
        <label htmlFor="institution" className="gv-label">
          Institution
        </label>
        <div className="relative">
          <select
            id="institution"
            value={institutionId}
            onChange={(e) => {
              setInstitutionId(e.target.value);
              setError('');
            }}
            className="gv-input appearance-none pr-9 cursor-pointer disabled:cursor-wait"
            disabled={institutionsLoading}
          >
            <option value="">
              {institutionsLoading ? 'Loading institutions…' : 'Select issuing institution…'}
            </option>
            {institutions.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </select>
          <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-navy-400" />
        </div>
        {institutionsError && (
          <p className="mt-1.5 flex flex-wrap items-center gap-x-2 text-xs text-red-600">
            {institutionsError}
            {onRetryInstitutions && (
              <button
                type="button"
                onClick={onRetryInstitutions}
                className="font-medium underline hover:text-red-700 cursor-pointer"
              >
                Retry
              </button>
            )}
          </p>
        )}
      </div>

      {/* Verify using */}
      <div>
        <span className="gv-label">Verify Using</span>
        <div className="inline-flex flex-wrap gap-1 rounded-3xl border border-navy-200 bg-navy-50/70 p-1">
          {(Object.keys(METHODS) as VerificationMethod[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => switchMethod(m)}
              aria-pressed={method === m}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition cursor-pointer ${
                method === m ? 'bg-navy-900 text-white' : 'text-navy-600 hover:text-navy-800'
              }`}
            >
              {METHODS[m].label}
            </button>
          ))}
        </div>
        {method === 'result' && (
          <p className="mt-1.5 text-xs text-navy-400">
            The verification code printed on the Statement of Result.
          </p>
        )}
      </div>

      {/* Reference */}
      <div>
        <label htmlFor="reference" className="gv-label">
          {current.inputLabel}
        </label>
        <input
          id="reference"
          type="text"
          value={reference}
          onChange={(e) => {
            setReference(e.target.value);
            setError('');
          }}
          placeholder={current.placeholder}
          className="gv-input"
          autoComplete="off"
          maxLength={64}
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <i className="ri-error-warning-line" />
          {error}
        </div>
      )}

      <Button
        type="submit"
        variant="primary"
        size="lg"
        loading={loading}
        disabled={disabled}
        className="w-full"
      >
        {current.submit}
      </Button>
    </form>
  );
}